import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Shield, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useOrgId } from '@/hooks/useOrgContext';

interface AdjustMedicationModalProps {
    medication: any;
    onClose: () => void;
    onSuccess: () => void;
}

export default function AdjustMedicationModal({
    medication,
    onClose,
    onSuccess,
}: AdjustMedicationModalProps) {
    const { user } = useAuth();
    const org_id = useOrgId(); // Get org_id from context
    const [transactionType, setTransactionType] = useState<string>('Received');
    const [quantity, setQuantity] = useState<number>(0);
    const [lotNumber, setLotNumber] = useState<string>('');
    const [referenceType, setReferenceType] = useState<string>('');
    const [referenceId, setReferenceId] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [witnessUserId, setWitnessUserId] = useState<string>('');
    const [witnessSignature, setWitnessSignature] = useState<string>('');
    const [allUsers, setAllUsers] = useState<any[]>([]);

    const isControlled = medication.is_controlled;
    const needsWitness = isControlled;

    // Calculate new quantity
    const currentQty = medication.quantity_on_hand || 0;
    let newQuantity = currentQty;
    
    if (transactionType === 'Received' || transactionType === 'Returned') {
        newQuantity = currentQty + quantity;
    } else if (transactionType === 'Used' || transactionType === 'Expired' || transactionType === 'Disposed') {
        newQuantity = currentQty - quantity;
    } else if (transactionType === 'Adjusted') {
        newQuantity = quantity; // Direct set
    }

    // Load users for witness dropdown
    useState(() => {
        if (needsWitness && org_id) {
            supabase
                .from('org_members')
                .select('user_id, email, first_name, last_name')
                .eq('org_id', org_id)
                .neq('user_id', user?.id)
                .then(({ data }) => setAllUsers(
                    (data || []).map(u => ({ ...u, full_name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email }))
                ));
        }
    });

    const adjustMutation = useMutation({
        mutationFn: async () => {
            // Validation
            if (quantity <= 0 && transactionType !== 'Adjusted') {
                throw new Error('Quantity must be greater than 0');
            }
            if (newQuantity < 0) {
                throw new Error('Cannot adjust to negative inventory');
            }
            if (needsWitness && (!witnessUserId || !witnessSignature)) {
                throw new Error('Witness and signature required for controlled substances');
            }

            // Insert transaction
            const { error: txError } = await supabase.from('or_medication_transactions').insert({
                org_id: medication.org_id,
                medication_id: medication.medication_id,
                transaction_type: transactionType,
                quantity_change: transactionType === 'Adjusted' ? quantity - currentQty : 
                                 (transactionType === 'Received' || transactionType === 'Returned' ? quantity : -quantity),
                quantity_after: newQuantity,
                lot_number: lotNumber || null,
                reference_type: referenceType || null,
                reference_id: referenceId || null,
                notes: notes || null,
                performed_by: user?.id,
                witness_user_id: needsWitness ? witnessUserId : null,
                witness_signature: needsWitness ? witnessSignature : null,
            });

            if (txError) throw txError;

            // Update medication quantity
            const { error: medError } = await supabase
                .from('or_medications')
                .update({ quantity_on_hand: newQuantity })
                .eq('medication_id', medication.medication_id);

            if (medError) throw medError;
        },
        onSuccess: () => {
            onSuccess();
            onClose();
        },
    });

    const canSubmit = 
        (transactionType === 'Adjusted' ? true : quantity > 0) &&
        newQuantity >= 0 &&
        (!needsWitness || (witnessUserId && witnessSignature));

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-pt-surface border border-pt-border-subtle rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-pt-surface border-b border-pt-border-subtle px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-pt-gold">Adjust Inventory</h2>
                    <button onClick={onClose} className="text-pt-text-muted hover:text-pt-gold">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Controlled Substance Warning */}
                    {isControlled && (
                        <div className="bg-red-900/20 border border-red-500/50 rounded p-4 flex items-start gap-3">
                            <Shield className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-semibold text-red-400">DEA Controlled Substance</div>
                                <div className="text-sm text-red-300 mt-1">
                                    Schedule {medication.dea_schedule} - Witness verification required
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Current Status */}
                    <div className="bg-pt-surface-light border border-pt-border-subtle rounded p-4">
                        <div className="text-sm text-pt-text-muted mb-2">Current Inventory</div>
                        <div className="text-2xl font-bold text-pt-gold">{currentQty} {medication.unit_of_measure}</div>
                    </div>

                    {/* Transaction Type */}
                    <div>
                        <label className="block text-sm font-medium text-pt-text-muted mb-2">
                            Transaction Type *
                        </label>
                        <select
                            value={transactionType}
                            onChange={(e) => setTransactionType(e.target.value)}
                            className="w-full bg-pt-surface-light border border-pt-border-subtle rounded px-3 py-2 text-pt-text"
                        >
                            <option value="Received">Received</option>
                            <option value="Used">Used</option>
                            <option value="Adjusted">Adjusted</option>
                            <option value="Expired">Expired</option>
                            <option value="Returned">Returned</option>
                            <option value="Disposed">Disposed</option>
                        </select>
                    </div>

                    {/* Quantity */}
                    <div>
                        <label className="block text-sm font-medium text-pt-text-muted mb-2">
                            {transactionType === 'Adjusted' ? 'New Quantity *' : 'Quantity *'}
                        </label>
                        <input
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(Number(e.target.value))}
                            min="0"
                            className="w-full bg-pt-surface-light border border-pt-border-subtle rounded px-3 py-2 text-pt-text"
                        />
                    </div>

                    {/* Before/After Display */}
                    <div className="grid grid-cols-3 gap-4 p-4 bg-pt-surface-light border border-pt-border-subtle rounded">
                        <div>
                            <div className="text-xs text-pt-text-muted mb-1">Before</div>
                            <div className="text-lg font-semibold text-pt-text">{currentQty}</div>
                        </div>
                        <div className="flex items-center justify-center">
                            <div className="text-pt-cyan">→</div>
                        </div>
                        <div>
                            <div className="text-xs text-pt-text-muted mb-1">After</div>
                            <div className={`text-lg font-semibold ${newQuantity < 0 ? 'text-red-400' : 'text-pt-gold'}`}>
                                {newQuantity}
                            </div>
                        </div>
                    </div>

                    {/* Lot Number */}
                    <div>
                        <label className="block text-sm font-medium text-pt-text-muted mb-2">
                            Lot Number
                        </label>
                        <input
                            type="text"
                            value={lotNumber}
                            onChange={(e) => setLotNumber(e.target.value)}
                            className="w-full bg-pt-surface-light border border-pt-border-subtle rounded px-3 py-2 text-pt-text"
                        />
                    </div>

                    {/* Reference */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-pt-text-muted mb-2">
                                Reference Type
                            </label>
                            <input
                                type="text"
                                value={referenceType}
                                onChange={(e) => setReferenceType(e.target.value)}
                                placeholder="e.g., Case, Order"
                                className="w-full bg-pt-surface-light border border-pt-border-subtle rounded px-3 py-2 text-pt-text"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-pt-text-muted mb-2">
                                Reference ID
                            </label>
                            <input
                                type="text"
                                value={referenceId}
                                onChange={(e) => setReferenceId(e.target.value)}
                                placeholder="e.g., CASE-123"
                                className="w-full bg-pt-surface-light border border-pt-border-subtle rounded px-3 py-2 text-pt-text"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-pt-text-muted mb-2">
                            Notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="w-full bg-pt-surface-light border border-pt-border-subtle rounded px-3 py-2 text-pt-text"
                        />
                    </div>

                    {/* Witness Section (Controlled Substances Only) */}
                    {needsWitness && (
                        <div className="bg-red-900/10 border border-red-500/30 rounded p-4 space-y-4">
                            <div className="flex items-center gap-2 text-red-400 font-semibold">
                                <Shield className="w-5 h-5" />
                                Witness Verification Required
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-pt-text-muted mb-2">
                                    Witness *
                                </label>
                                <select
                                    value={witnessUserId}
                                    onChange={(e) => setWitnessUserId(e.target.value)}
                                    className="w-full bg-pt-surface-light border border-red-500/50 rounded px-3 py-2 text-pt-text"
                                >
                                    <option value="">Select witness...</option>
                                    {allUsers.map((u) => (
                                        <option key={u.user_id} value={u.user_id}>
                                            {u.full_name || u.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-pt-text-muted mb-2">
                                    Witness Signature (Initials) *
                                </label>
                                <input
                                    type="text"
                                    value={witnessSignature}
                                    onChange={(e) => setWitnessSignature(e.target.value)}
                                    maxLength={10}
                                    placeholder="Enter initials"
                                    className="w-full bg-pt-surface-light border border-red-500/50 rounded px-3 py-2 text-pt-text"
                                />
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {newQuantity < 0 && (
                        <div className="text-red-400 text-sm">
                            Cannot adjust to negative inventory
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 bg-pt-surface border-t border-pt-border-subtle px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-pt-border rounded text-pt-text-muted hover:text-pt-text hover:border-pt-border-strong"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => adjustMutation.mutate()}
                        disabled={!canSubmit || adjustMutation.isPending}
                        className="px-4 py-2 bg-pt-gold text-pt-navy rounded font-semibold hover:bg-pt-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {adjustMutation.isPending ? 'Saving...' : 'Confirm Adjustment'}
                    </button>
                </div>
            </div>
        </div>
    );
}
